import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MenuGroup, MenuPage, MenuPermission, PageMeta } from '../models/menu-item.model';
import { AppNavigationEntry } from '../config/app-navigation.config';
import {
    ArrayResponseEnvelopeOfIdentityMenuModuleDto,
    IdentityMenuModuleDto,
    IdentityMenuPermissionDto,
} from '../models/auth-api.models';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { LoggerService } from './logger.service';
import { APP_NAVIGATION } from '../config/app-navigation.config';
import { ROUTE_CATALOG } from '../config/route-catalog.config';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class MenuService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly permissionService = inject(PermissionService);
    private readonly logger = inject(LoggerService);

    readonly menuGroups = signal<MenuGroup[]>([]);
    private pageIdByRoute = new Map<string, number>();

    private readonly routeByIdentifier = new Map(
        ROUTE_CATALOG.map((entry) => [
            this.toLookupKey(entry.moduleIdentifier, entry.sectionIdentifier, entry.pageIdentifier),
            entry.routerLink,
        ]),
    );

    loadMenuItems(): Observable<MenuGroup[]> {
        return this.http
            .get<ArrayResponseEnvelopeOfIdentityMenuModuleDto>(`${environment.identityApiUrl}/identity/user/menu`)
            .pipe(
                map((response) => {
                    const modules = response.results ?? [];
                    this.pageIdByRoute = this.buildPageIdByRoute(modules);
                    return this.mapIdentityMenuToGroups(modules);
                }),
                map((groups) => this.mergeAdminMenuEntries(groups)),
                map((groups) => this.dedupeMenuGroups(groups)),
                map((groups) => {
                    const resolved = groups.length > 0 ? groups : this.buildFallbackMenu();
                    if (groups.length === 0) {
                        this.pageIdByRoute = new Map<string, number>();
                    }
                    this.menuGroups.set(resolved);
                    return resolved;
                }),
                catchError((error) => {
                    this.logger.error('MenuService: Failed to load /identity/user/menu, using fallback', error);
                    this.pageIdByRoute = new Map<string, number>();
                    const fallback = this.buildFallbackMenu();
                    this.menuGroups.set(fallback);
                    return of(fallback);
                }),
            );
    }

    getCurrentPageId(url: string = this.router.url): number | null {
        const currentPath = this.normalizeRoute(url);
        let currentPageId: number | null = null;
        let longestMatchLength = -1;

        for (const [route, pageId] of this.pageIdByRoute.entries()) {
            if (!this.matchesRoute(route, currentPath) || route.length <= longestMatchLength) {
                continue;
            }

            currentPageId = pageId;
            longestMatchLength = route.length;
        }

        return currentPageId;
    }

    resolvePageMeta(url: string = this.router.url): PageMeta {
        const currentPath = this.normalizeRoute(url);

        let bestEntry: AppNavigationEntry | null = null;
        let bestLength = -1;
        for (const entry of APP_NAVIGATION) {
            const route = this.normalizeRoute(entry.routerLink);
            if (this.matchesRoute(route, currentPath) && route.length > bestLength) {
                bestEntry = entry;
                bestLength = route.length;
            }
        }

        if (bestEntry) {
            const crumbs = [bestEntry.moduleLabel, bestEntry.sectionLabel, bestEntry.pageLabel].filter(Boolean);
            return {
                title: bestEntry.pageLabel,
                icon: bestEntry.icon ?? this.getIconForModule(bestEntry.moduleIdentifier),
                description: bestEntry.description,
                crumbs,
            };
        }

        for (const group of this.menuGroups()) {
            for (const section of group.sections) {
                for (const page of section.pages) {
                    if (page.routerLink && this.normalizeRoute(page.routerLink) === currentPath) {
                        return {
                            title: page.label,
                            icon: page.icon ?? group.icon ?? '',
                            crumbs: [group.label, section.label, page.label].filter(Boolean),
                        };
                    }
                }
            }
        }

        if (currentPath === '/dashboard') {
            return { title: 'Dashboard', icon: '', crumbs: ['Dashboard'] };
        }
        if (currentPath === '/perfil') {
            return { title: 'Perfil', icon: '', crumbs: ['Perfil'] };
        }

        return { title: '', icon: '', crumbs: [] };
    }

    private buildFallbackMenu(): MenuGroup[] {
        const isAdmin = this.authService.isRbiAdmin();
        const canViewUsers = this.permissionService.canViewUsers();

        const visibleEntries = isAdmin
            ? APP_NAVIGATION
            : APP_NAVIGATION.filter((entry) =>
                  this.permissionService.canAccess(
                      entry.moduleIdentifier,
                      entry.sectionIdentifier,
                      entry.pageIdentifier,
                  ),
              );

        const groups = this.mapEntriesToMenu(visibleEntries);
        if (isAdmin) {
            groups.push(this.getAdminMenuGroup());
        } else if (canViewUsers) {
            groups.push(this.getUserManagementMenuGroup());
        }

        return this.dedupeMenuGroups(groups);
    }

    private mergeAdminMenuEntries(groups: MenuGroup[]): MenuGroup[] {
        if (!this.authService.isRbiAdmin()) {
            return groups;
        }

        const adminGroup = this.getAdminMenuGroup();
        const nextGroups = groups.map((group) => ({
            ...group,
            sections: group.sections.map((section) => ({
                ...section,
                pages: [...section.pages],
            })),
        }));

        const existingAdminGroup = nextGroups.find(
            (group) => this.toLabelKey(group.label) === this.toLabelKey(adminGroup.label),
        );

        if (!existingAdminGroup) {
            return [...nextGroups, adminGroup];
        }

        for (const adminSection of adminGroup.sections) {
            const existingSection = existingAdminGroup.sections.find(
                (section) => this.toLabelKey(section.label) === this.toLabelKey(adminSection.label),
            );

            if (!existingSection) {
                existingAdminGroup.sections.push({
                    ...adminSection,
                    pages: [...adminSection.pages],
                });
                continue;
            }

            for (const adminPage of adminSection.pages) {
                const adminRouterLink = (adminPage.routerLink ?? '').trim().toLowerCase();
                const pageExists = existingSection.pages.some(
                    (page) => (page.routerLink ?? '').trim().toLowerCase() === adminRouterLink,
                );

                if (!pageExists) {
                    existingSection.pages.push({ ...adminPage });
                }
            }
        }

        return nextGroups;
    }

    private mapIdentityMenuToGroups(modules: IdentityMenuModuleDto[]): MenuGroup[] {
        let generatedSectionId = 4000;
        let generatedPageId = 9000;

        const groups: Array<MenuGroup | null> = modules.map((module, moduleIndex) => {
            const moduleIdentifier = this.pickIdentifier(module.permissionIdentifier, module.name);

            const sections = (module.sections ?? [])
                .map((section) => {
                    const sectionIdentifier = this.pickIdentifier(section.permissionIdentifier, section.name);

                    const pages = (section.pages ?? [])
                        .map((page) => {
                            const pageIdentifier = this.pickIdentifier(page.permissionIdentifier, page.name);
                            const routerLink = this.resolveRouterLink(
                                moduleIdentifier,
                                sectionIdentifier,
                                pageIdentifier,
                                page.name,
                            );
                            const permissions = this.mapIdentityPermissions(page.permissions ?? []);

                            if (!routerLink) {
                                return null;
                            }

                            return {
                                id: page.id ?? generatedPageId++,
                                label: page.name?.trim() || pageIdentifier || 'Sin nombre',
                                routerLink,
                                permissions,
                                disabled: false,
                            };
                        })
                        .filter((page): page is NonNullable<typeof page> => page !== null);

                    if (pages.length === 0) {
                        return null;
                    }

                    return {
                        id: section.id ?? generatedSectionId++,
                        label: section.name?.trim() || sectionIdentifier || 'Seccion',
                        pages,
                    };
                })
                .filter((section): section is NonNullable<typeof section> => section !== null);

            if (sections.length === 0) {
                return null;
            }

            const group: MenuGroup = {
                id: module.id ?? moduleIndex + 1,
                label: module.name?.trim() || moduleIdentifier || 'Modulo',
                icon: this.getIconForModule(moduleIdentifier || module.name || ''),
                isGroup: true,
                sections,
            };

            return group;
        });

        return groups.filter((group): group is MenuGroup => group !== null);
    }

    private mapIdentityPermissions(permissions: IdentityMenuPermissionDto[]): MenuPermission[] {
        return permissions.map((permission, index) => ({
            id: index + 1,
            name: permission.permissionIdentifier || permission.action || 'Permiso',
            action: permission.action || 'read',
        }));
    }

    private resolveRouterLink(
        moduleIdentifier: string,
        sectionIdentifier: string,
        pageIdentifier: string,
        pageName?: string,
    ): string | undefined {
        const byIdentifier = this.routeByIdentifier.get(
            this.toLookupKey(moduleIdentifier, sectionIdentifier, pageIdentifier),
        );
        if (byIdentifier) {
            return byIdentifier;
        }

        const normalizedPage = (pageName ?? pageIdentifier).toLowerCase().trim();
        if (normalizedPage === 'dashboard') {
            return '/dashboard';
        }
        if (normalizedPage === 'usuarios' || normalizedPage === 'users') {
            return '/admin/usuarios';
        }
        if (normalizedPage === 'roles') {
            return '/admin/roles';
        }
        if (normalizedPage === 'paginas' || normalizedPage === 'pages') {
            return '/admin/paginas';
        }

        return undefined;
    }

    private buildPageIdByRoute(modules: IdentityMenuModuleDto[]): Map<string, number> {
        const pageIds = new Map<string, number>();

        for (const module of modules) {
            const moduleIdentifier = this.pickIdentifier(module.permissionIdentifier, module.name);

            for (const section of module.sections ?? []) {
                const sectionIdentifier = this.pickIdentifier(section.permissionIdentifier, section.name);

                for (const page of section.pages ?? []) {
                    if (!this.isValidPageId(page.id)) {
                        continue;
                    }

                    const pageIdentifier = this.pickIdentifier(page.permissionIdentifier, page.name);
                    const routerLink = this.resolveRouterLink(
                        moduleIdentifier,
                        sectionIdentifier,
                        pageIdentifier,
                        page.name,
                    );

                    if (!routerLink) {
                        continue;
                    }

                    const normalizedRoute = this.normalizeRoute(routerLink);
                    if (!pageIds.has(normalizedRoute)) {
                        pageIds.set(normalizedRoute, page.id!);
                    }
                }
            }
        }

        return pageIds;
    }

    private isValidPageId(pageId: number | undefined): pageId is number {
        return typeof pageId === 'number' && Number.isInteger(pageId) && pageId > 0;
    }

    private pickIdentifier(primary?: string, fallback?: string): string {
        return (primary ?? fallback ?? '').toLowerCase().trim();
    }

    private toLookupKey(moduleIdentifier: string, sectionIdentifier: string, pageIdentifier: string): string {
        return `${moduleIdentifier.toLowerCase().trim()}:${sectionIdentifier.toLowerCase().trim()}:${pageIdentifier.toLowerCase().trim()}`;
    }

    private getAdminMenuGroup(): MenuGroup {
        return {
            id: 9990,
            label: 'ADMINISTRACION',
            icon: '',
            isGroup: true,
            sections: [
                {
                    id: 9991,
                    label: 'Sistema',
                    pages: [
                        { id: 9992, label: 'Usuarios', routerLink: '/admin/usuarios', permissions: [], disabled: false },
                        { id: 9993, label: 'Paginas', routerLink: '/admin/paginas', permissions: [], disabled: false },
                        { id: 9994, label: 'Roles', routerLink: '/admin/roles', permissions: [], disabled: false },
                    ],
                },
            ],
        };
    }

    private getUserManagementMenuGroup(): MenuGroup {
        return {
            id: 9990,
            label: 'ADMINISTRACION',
            icon: '',
            isGroup: true,
            sections: [
                {
                    id: 9991,
                    label: 'Sistema',
                    pages: [
                        { id: 9992, label: 'Usuarios', routerLink: '/admin/usuarios', permissions: [], disabled: false },
                    ],
                },
            ],
        };
    }

    private mapEntriesToMenu(entries: typeof APP_NAVIGATION): MenuGroup[] {
        if (!entries.length) {
            return [];
        }

        const groupsMap = new Map<string, MenuGroup>();
        let nextSectionId = 1000;
        let nextPageId = 10000;

        entries.forEach((entry, index) => {
            const groupKey = entry.moduleIdentifier;
            const group = groupsMap.get(groupKey) ?? {
                id: index + 1,
                label: entry.moduleLabel,
                icon: this.getIconForModule(entry.moduleIdentifier),
                isGroup: true,
                sections: [],
            };

            let section = group.sections.find((item) => item.label === entry.sectionLabel);
            if (!section) {
                section = { id: nextSectionId++, label: entry.sectionLabel, pages: [] };
                group.sections.push(section);
            }

            section.pages.push({
                id: nextPageId++,
                label: entry.pageLabel,
                routerLink: entry.routerLink,
                permissions: [],
                disabled: false,
            });

            groupsMap.set(groupKey, group);
        });

        return Array.from(groupsMap.values());
    }

    /**
     * Clave para comparar etiquetas de menú.
     *
     * Ignora mayúsculas y acentos: el API devuelve «Administración» mientras el
     * grupo que se inyecta por código dice «ADMINISTRACION», y sin normalizar
     * la tilde se mostrarían como dos grupos distintos.
     */
    private toLabelKey(label: string): string {
        return label
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '');
    }

    private dedupeMenuGroups(groups: MenuGroup[]): MenuGroup[] {
        const mergedGroups = new Map<string, MenuGroup>();

        for (const group of groups) {
            const groupKey = this.toLabelKey(group.label);
            const existingGroup = mergedGroups.get(groupKey);

            if (!existingGroup) {
                mergedGroups.set(groupKey, {
                    ...group,
                    sections: group.sections.map((section) => ({
                        ...section,
                        pages: [...section.pages],
                    })),
                });
                continue;
            }

            for (const section of group.sections) {
                const sectionKey = this.toLabelKey(section.label);
                const existingSection = existingGroup.sections.find(
                    (item) => this.toLabelKey(item.label) === sectionKey,
                );

                if (!existingSection) {
                    existingGroup.sections.push({ ...section, pages: [...section.pages] });
                    continue;
                }

                existingSection.pages.push(...section.pages);
            }
        }

        const seenRoutes = new Set<string>();

        return Array.from(mergedGroups.values())
            .map((group) => {
                const sections = group.sections
                    .map((section) => {
                        const pages = section.pages.filter((page) => {
                            const routeKey = (page.routerLink ?? '').trim().toLowerCase();
                            const fallbackKey = `${section.label}:${page.label}`.trim().toLowerCase();
                            const pageKey = routeKey || fallbackKey;

                            if (seenRoutes.has(pageKey)) {
                                return false;
                            }

                            seenRoutes.add(pageKey);
                            return true;
                        });

                        if (!pages.length) {
                            return null;
                        }

                        return { ...section, pages };
                    })
                    .filter((section): section is NonNullable<typeof section> => section !== null);

                if (!sections.length) {
                    return null;
                }

                return { ...group, sections };
            })
            .filter((group): group is NonNullable<typeof group> => group !== null);
    }

    private normalizeRoute(url: string): string {
        const [path] = url.split(/[?#]/, 1);
        if (!path) {
            return '/';
        }

        const normalizedPath = path.trim().replace(/\/+$/, '');
        return normalizedPath || '/';
    }

    private matchesRoute(route: string, currentPath: string): boolean {
        return currentPath === route || currentPath.startsWith(`${route}/`);
    }

    private getIconForModule(moduleId: string): string {
        switch (moduleId.toLowerCase()) {
            case 'administracion':
            case 'identity':
                return '';
            default:
                return '';
        }
    }
}
